import type { FC } from 'react'
import { useCallback, useState } from 'react'
import styled from '@emotion/styled'

import sanitizeHtml from 'sanitize-html'

import type { FieldValues } from 'react-hook-form'

import { useRouter } from 'next/router'

import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import ButtonText from '@segment/matcha/components/ButtonText'

import HeroRecipeBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import SignUpForm from '@segment/templates/Forms/SignUpForm'

import { trackButtonClick, trackErrorSignupForm } from '@segment/tracking/scripts'

import { fetchUserExists, fetchSignup } from '@segment/api/staticSiteBackend/signup'

import heroPng2x from './assets/Hero@2x.png'
import heroWebp from './assets/Hero.webp'
import heroWebp2x from './assets/Hero@2x.webp'

const talkToSalesButton = {
  title: `Talk to sales`,
  link: `/demo/`,
}

interface HeroProps {
  heading: string
  copy: string
  formId: string
}
const Hero: FC<HeroProps> = ({ heading, copy, formId }) => {
  const [formSize, setFormSize] = useState(417)
  const router = useRouter()

  const handleEmailValidation = useCallback(async (email: string) => {
    const controller = new AbortController()
    const response = await fetchUserExists(controller.signal, email)
    const valid = response.userExists === false
    return valid
  }, [])

  const handleSubmit = useCallback(
    async (data: FieldValues) => {
      const { name, email, password } = data
      const controller = new AbortController()
      const response = await fetchSignup(controller.signal, { name, email, password })
      if (response.success && response.redirectUrl) {
        router.push(response.redirectUrl)
      } else {
        trackErrorSignupForm({ name, email }, String(response.message))
        setFormSize(440)
        throw new Error(response.message)
      }
    },
    [router]
  )

  return (
    <Wrapper id="sign-up-hero-form">
      <StyledSectionBase variant="centered">
        <Section.Copy>
          <CopyTextBase centered={true}>
            <CopyTextHeading tag="h1" variant="h2">
              {heading}
            </CopyTextHeading>
            <CopyText.Description
              tag="p"
              variant="pLarge"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(copy, { allowedTags: [`a`] }),
              }}
            />
          </CopyTextBase>
        </Section.Copy>
      </StyledSectionBase>
      <HeroRecipeBase variant="2Col" copyVariant="wide">
        <HeroRecipeCopy>
          <FormWrapper size={formSize}>
            <SignUpForm
              customFormIdentifier={formId}
              customTrackingType={formId}
              onSubmit={handleSubmit}
              emailValidator={handleEmailValidation}
            />
            <StyledButtonText
              href={talkToSalesButton.link}
              onClick={() =>
                trackButtonClick({ buttonText: talkToSalesButton.title, targetUrl: talkToSalesButton.link })
              }
              iconEnd={ChevronRightIcon}
            >
              Talk to sales
            </StyledButtonText>
          </FormWrapper>
        </HeroRecipeCopy>
        <HeroRecipe.Contents>
          <picture>
            <source type="image/webp" srcSet={`${heroWebp}, ${heroWebp2x} 2x`} />
            <HeroRecipe.Image width="640" height="400" src={heroPng2x} alt="Illustration: Dashboard" loading="lazy" />
          </picture>
        </HeroRecipe.Contents>
      </HeroRecipeBase>
    </Wrapper>
  )
}

export default Hero

const Wrapper = styled.div`
  background: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const HeroRecipeCopy = styled(HeroRecipe.Copy)`
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    margin: 0 auto;
  }
`

const CopyTextHeading = styled(CopyText.Heading)`
  max-width: 220px;
  ${({ theme }) => theme.breakpoints.up(`xs`)} {
    max-width: unset;
  }
`

const StyledSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    gap: 0;
    padding-bottom: 0;
  }
`
interface FormWrapperProps {
  size: number
}
const FormWrapper = styled.div<FormWrapperProps>`
  max-width: ${({ size }) => `${size}px`};
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    max-width: 100%;
  }
`

const StyledButtonText = styled(ButtonText)`
  padding-top: 24px;
`
